import { ComponentCoordinate } from 'api3';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, ViewChild, computed, effect, input, signal, untracked } from '@angular/core';
import { ZoomContainerComponent } from '@app/components/zoom/zoom-container/zoom-container.component';
import { SystemImage } from '../hgs-location.component';

@Component({
  selector: 'app-hgs-svg-fusebox',
  template: `
    <div [attr.class]="'border border-slate-200 overflow-hidden rounded-lg ' + (imgClass() ?? '')">
      @if (imageUrl()) {
        <app-zoom-container [minScale]="0.5" [maxScale]="10">
          <svg
            #svgElement
            [attr.viewBox]="'0 0 ' + imageWidth() + ' ' + imageHeight()"
            class="h-full w-full object-contain lg:max-h-[30dvh] lg:min-h-[20dvh] 2xl:max-h-[45dvh]"
            xmlns="http://www.w3.org/2000/svg">
            <image
              [attr.href]="imageUrl()"
              [attr.width]="imageWidth()"
              [attr.height]="imageHeight()"
              x="0"
              y="0"
              preserveAspectRatio="xMidYMid meet" />
          </svg>
        </app-zoom-container>
      }
    </div>
  `,
  imports: [CommonModule, ZoomContainerComponent],
  standalone: true,
})
export class HgsSvgFuseboxComponent {
  @ViewChild('svgElement') svgElement?: ElementRef<SVGSVGElement>;

  imgClass = input<string>();
  system = input<SystemImage | undefined>();
  shownBox = input<ComponentCoordinate | undefined>();

  imageUrl = computed(() => this.system()?.imagePlacementUrl ?? undefined);
  imageWidth = signal<number>(800);
  imageHeight = signal<number>(600);
  boxWidth = computed(() => this.imageWidth() * 0.09);
  boxHeight = computed(() => this.imageHeight() * 0.08);
  drawnBoxes = signal<Record<string, SVGRectElement>>({});

  onChange = effect(() => {
    const sys = this.system();
    if (sys?.imagePlacementUrl) {
      this._loadImageDimensions(sys.imagePlacementUrl);
    }
  });

  onShowBox = effect(() => {
    const box = this.shownBox();

    untracked(() => {
      this._hideAllBoxes();
      if (box) {
        const rect = this._drawBox({ x1: box.x1, y1: box.y1, x2: box.x2, y2: box.y2 });
        if (rect) {
          this.drawnBoxes.update((boxes) => ({ ...boxes, [`box-${JSON.stringify({ x1: box.x1, y1: box.y1, x2: box.x2, y2: box.y2 })}`]: rect }));
        }
      }
    });
  });

  private _drawBox(coords: { x1: number; y1: number; x2: number; y2: number }): SVGRectElement | undefined {
    if (!this.svgElement?.nativeElement) return;

    const id = `box-box-${coords.x1}-${coords.y1}-${coords.x2}-${coords.y2}`;

    let x = Math.min(coords.x1, coords.x2) * this.imageWidth();
    let y = Math.min(coords.y1, coords.y2) * this.imageHeight();
    let width = Math.abs(coords.x2 - coords.x1) * this.imageWidth();
    let height = Math.abs(coords.y2 - coords.y1) * this.imageHeight();

    // if rect is too small, draw it larger
    const minSizeInPercent = 5;
    let minWidth = (this.imageWidth() / 100) * minSizeInPercent;
    let minHeight = (this.imageHeight() / 100) * minSizeInPercent;
    if (minWidth < 20) minWidth = 20;
    if (minHeight < 20) minHeight = 20;

    const scaleFactor = 1.5;
    if (width < minWidth) {
      if (width <= 3) width = 5;
      width = width * scaleFactor;
      x = x - (width - Math.abs(coords.x2 - coords.x1) * this.imageWidth()) / 2;
    }

    if (height < minHeight) {
      if (height <= 3) height = 5;
      height = height * scaleFactor;
      y = y - (height - Math.abs(coords.y2 - coords.y1) * this.imageHeight()) / 2;
    }

    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('id', id);
    rect.setAttribute('x', x.toString());
    rect.setAttribute('y', y.toString());
    rect.setAttribute('width', width.toString());
    rect.setAttribute('height', height.toString());
    rect.setAttribute('fill', '#ccff013c');
    rect.setAttribute('stroke', '#010ca2');
    rect.setAttribute('stroke-width', this.imageWidth() > 1000 ? '4' : '2');

    this.svgElement.nativeElement.appendChild(rect);

    return rect;
  }

  private _hideAllBoxes(): void {
    if (!this.svgElement?.nativeElement) return;
    Object.keys(this.drawnBoxes()).forEach((boxId) => this._hideBox(boxId));
  }

  private _hideBox(id: string | undefined, coords?: { x1: number; y1: number; x2: number; y2: number }): void {
    if (!this.svgElement?.nativeElement) return;

    const boxId = id ?? `box-${JSON.stringify(coords)}`;

    const rect = this.drawnBoxes()[boxId];
    if (rect) {
      this.svgElement.nativeElement.removeChild(rect);
    } else {
      const rectById = this.svgElement.nativeElement.getElementById(boxId);
      if (rectById) {
        this.svgElement.nativeElement.removeChild(rectById);
      }
    }

    this.drawnBoxes.update((boxes) => {
      const updated = { ...boxes };
      delete updated[boxId];
      return updated;
    });
  }

  private _loadImageDimensions(url: string): void {
    const img = new Image();
    img.onload = () => {
      this.imageWidth.set(img.width);
      this.imageHeight.set(img.height);
    };
    img.onerror = () => {
      // TODO: proper error handling
      console.error('Failed to load image for wiring diagram from URL:', url);
    };
    img.src = url;
  }
}
